import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useUser } from "../UserContext";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

interface account {
  id: number;
  account: string;
  state_code: string;
  city: string;
}

interface Product {
  product_id: number;
  product: string;
  brand_id: number;
}

const PitchAdd: React.FC = () => {
  const { currentUser } = useUser();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    pitch_name: "",
    pricing: "",
    offer: "",
    selectedFile: null as File | null,
  });
  const [accounts, setaccounts] = useState<account[]>([]);
  const [selectedaccounts, setSelectedaccounts] = useState<account[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({
        ...prev,
        selectedFile: e.target.files![0],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let deck_url = "";
      if (formData.selectedFile) {
        const uploadData = new FormData();
        uploadData.append("file", formData.selectedFile);
        uploadData.append("userId", currentUser?.user_id?.toString() || "");

        const uploadResponse = await axios.post(
          "/api/upload-pitch-deck",
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (!uploadResponse.data.fileUrl) {
          throw new Error("File upload failed - no URL returned");
        }

        deck_url = uploadResponse.data.fileUrl;
      }

      for (const account of selectedaccounts) {
        for (const product of selectedProducts) {
          if (product.product_id === undefined) {
            console.error("Product ID is undefined for product:", product);
            continue;
          }

          const pitchData = {
            user_id: currentUser?.user_id,
            account_id: account.id,
            product_id: product.product_id,
            pitch_name: formData.pitch_name,
            pricing: formData.pricing,
            offer: formData.offer || null,
            deck_url: deck_url || null,
          };
          const response = await axios.post(
            `${import.meta.env.VITE_API_URL}/pitches/pitches`,
            pitchData
          );
          console.log("Pitch submitted:", response.data);
        }
      }

      handleClose();
    } catch (error) {
      console.error("Error in submission:", error);
      if (axios.isAxiosError(error)) {
        console.error("Response data:", error.response?.data);
        console.error("Response status:", error.response?.status);
      }
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchaccounts();
      fetchProducts();
    }
  }, [currentUser]);

  const fetchaccounts = async () => {
    try {
      const statesResponse = await axios.get(
        `${import.meta.env.VITE_API_URL}/users/user-states`,
        {
          params: {
            userId: currentUser?.user_id,
            type: "brand",
          },
        }
      );

      const accountsResponse = await axios.get(
        `${import.meta.env.VITE_API_URL}/accounts/accounts-by-states`,
        {
          params: {
            states: statesResponse.data.map((s: any) => s.state_code).join(","),
          },
        }
      );

      setaccounts(accountsResponse.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/brands/brand-products`,
        {
          params: {
            userId: currentUser?.user_id,
          },
        }
      );
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<CloudUploadIcon />}
      >
        Pitch account
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pitch-modal-title"
      >
        <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
          <Typography id="pitch-modal-title" variant="h6" component="h2" mb={3}>
            Submit a New Pitch
          </Typography>

          <Autocomplete
            multiple
            limitTags={1}
            id="accounts-select"
            options={accounts}
            getOptionLabel={(option) =>
              `${option.account} - ${option.city}, ${option.state_code}`
            }
            value={selectedaccounts}
            onChange={(_, newValue) => {
              setSelectedaccounts(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select accounts"
                placeholder={
                  selectedaccounts.length > 0 ? "" : "Search accounts..."
                }
                margin="normal"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Typography>
                  {option.account} - {option.city}, {option.state_code}
                </Typography>
              </Box>
            )}
          />

          <Autocomplete
            multiple
            limitTags={1}
            id="products-select"
            options={products}
            getOptionLabel={(option) => option.product}
            value={selectedProducts}
            onChange={(_, newValue) => {
              setSelectedProducts(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Products"
                placeholder={
                  selectedProducts.length > 0 ? "" : "Search products..."
                }
                margin="normal"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.product_id}>
                <Typography>{option.product}</Typography>
              </Box>
            )}
          />

          <TextField
            fullWidth
            label="Pitch Name"
            name="pitch_name"
            value={formData.pitch_name}
            onChange={handleInputChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Pricing"
            name="pricing"
            value={formData.pricing}
            onChange={handleInputChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Offer"
            name="offer"
            value={formData.offer}
            onChange={handleInputChange}
            margin="normal"
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              accept="application/pdf"
              style={{ display: "none" }}
              id="pitch-deck-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="pitch-deck-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Upload Pitch Deck (PDF)
              </Button>
            </label>
            {formData.selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected file: {formData.selectedFile.name}
              </Typography>
            )}
          </Box>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Submit Pitch
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PitchAdd;
